import type { FC, MouseEvent } from 'react';
import { memo } from 'react';

import { AvatarSizeEnum } from '@shared/enums/avatarSize.enum';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import useAddToAssignList from '@shared/hooks/useAddToAssignList';
import ShareUserButton from '@shared/ui-kit/avatars/MuiAvatarGroup/components/ShareUserButton';
import { ownerCheckedUsersArr } from '@shared/ui-kit/avatars/MuiAvatarGroup/utils/ownerCheckedUsersArr';
import { sortDataForUserList } from '@shared/ui-kit/avatars/MuiAvatarGroup/utils/sortDataForUserList';
import modalObserver from '@shared/utils/observers/modalObserver';
import { AvatarContainer, MuiPreloader } from 'shared/ui-kit';

import {
  AvatarGroupContainer,
  AvatarGroupContentContainer,
  LoadingConfirmShareContainer,
} from './MuiAvatarGroup.style';

import type { ItemUserModel } from '@shared/models/itemUser.model';
import type { MuiAvatarGroupProps } from '@shared/ui-kit/avatars/MuiAvatarGroup/types';

// TODO ADD SIZE ENUMS
export const MuiAvatarGroup: FC<MuiAvatarGroupProps> = memo(
  ({
    users,
    owner,
    handleConfirmSharePopup,
    maxItemView = 2,
    isShowUserListPopover = true,
    isContainOwnerInUsers = true,
    isShowAddUserBtn = true,
    size = AvatarSizeEnum.size28,
    defaultStatusForOwner,
    onClickShare,
    isCanChangeStatus,
    isShowStatusesForViewer,
    isCanOwnerChangeYourPermission,
    isLoadingConfirmShare,
    classNameForTargetUserOnboardingTooltip,
    handleDeleteCurrentUser,
  }) => {
    const handleOpenAssignedUsersListModal = () => {
      modalObserver.addModal(ModalNamesEnum.assignedUsersListModal, {
        props: {
          handleDeleteCurrentUser,
          usersList: sortDataForUserList(users, owner),
          owner,
          handleConfirm: handleConfirmSharePopup,
          defaultStatusForOwner,
          isCanOwnerChangeYourPermission,
          isCanChangeStatus,
          isShowStatusesForViewer,
          isEditMode: isShowAddUserBtn,
        },
      });
    };

    const handleAvatarGroupClick = (e: MouseEvent<HTMLDivElement>) => {
      if (isShowUserListPopover) {
        e.stopPropagation();
        handleOpenAssignedUsersListModal();
      }
    };

    const isUsersListWithOwner = !!ownerCheckedUsersArr(users, owner, isContainOwnerInUsers)?.length;

    const usersWithOwnerFlag: ItemUserModel[] = users.map((user) => ({
      ...user,
      isOwner: owner.id === user.id,
    }));

    const { handleOpenInviteModal } = useAddToAssignList(usersWithOwnerFlag, handleConfirmSharePopup);

    return (
      <AvatarGroupContentContainer isUsersListWithOwner={isUsersListWithOwner}>
        <AvatarGroupContainer size={size} max={maxItemView} onClick={handleAvatarGroupClick}>
          {ownerCheckedUsersArr(users, owner, isContainOwnerInUsers).map((item) => (
            <AvatarContainer
              key={item.id}
              firstName={item.contacts?.is_company ? item.contacts?.company : item.first_name}
              lastName={item.contacts?.is_company ? '' : item.last_name || ''}
              customStyle={{ '& .BaseBadge-badge': { right: '6px' } }}
              src={
                item?.avatar ? item?.avatar?.additional_info?.size_urls?.avatar_icon || item?.avatar?.url : ''
              }
              id={item.id}
              isOwner={owner.id === item.id}
              size={size}
            />
          ))}
        </AvatarGroupContainer>

        {isShowAddUserBtn && (
          <>
            {isLoadingConfirmShare ? (
              <LoadingConfirmShareContainer isUsersListWithOwner={isUsersListWithOwner}>
                <MuiPreloader size="medium" isShow />
              </LoadingConfirmShareContainer>
            ) : (
              <ShareUserButton
                size={size}
                isUsersListWithOwner={isUsersListWithOwner}
                onClickShare={onClickShare || handleOpenInviteModal}
                classNameForTargetUserOnboardingTooltip={classNameForTargetUserOnboardingTooltip}
              />
            )}
          </>
        )}
      </AvatarGroupContentContainer>
    );
  },
);
